// Prompt library + favourites API.
//
// Backend contract (src/api_layer/routers/prompt_library.py):
//   GET    /prompt-library?audience=SALESREP|RBD|LEADERSHIP|EXTERNAL_SOURCES
//            -> { audience, categories: { [name]: PromptRow[] } }   (audience optional;
//               falls back to the caller's business_function. Catalog rows carry
//               prompt_id + is_favorite, but favorite_id is ALWAYS null here.)
//   GET    /prompt-library/favorites         -> { user_email, favorites: PromptRow[] }
//            (every row has favorite_id; prompt_id null for custom rows)
//   POST   /prompt-library/favorites         { prompt_id }
//            -> { status, favorite_id, prompt_id, is_favorite:true, is_custom:false }
//   POST   /prompt-library/favorites/custom  { prompt, category?, audience? }
//            -> { status, favorite_id, prompt_id:null, is_favorite:true, is_custom:true }
//   DELETE /prompt-library/favorites/:favorite_id   (STRING favorite_id, not prompt_id)
//            -> { status, favorite_id, prompt_id:null, is_favorite:false }
//
// Auth is the HttpOnly session cookie (withCredentials) plus the Bearer/user
// headers in dev — identical to every other slice in this app.

import axios from "axios";
import { getBaseUrl } from "../redux/config";
import { getAuthApiHeaders } from "../utils/common";

const baseUrl = getBaseUrl();

// Shared request config: session cookie + standard auth headers.
const authConfig = (extra = {}) => ({
  headers: {
    "Content-Type": "application/json",
    "use-case": "src",
    ...getAuthApiHeaders(),
  },
  withCredentials: true,
  ...extra,
});

// GET /prompt-library — omit the audience param unless an explicit override is
// needed (the backend resolves it from the JWT otherwise).
export const getPromptLibrary = async (audience) => {
  const config = authConfig(audience ? { params: { audience } } : {});
  const res = await axios.get(`${baseUrl}/prompt-library`, config);
  return res.data;
};

// GET /prompt-library/favorites
export const getFavorites = async () => {
  const res = await axios.get(
    `${baseUrl}/prompt-library/favorites`,
    authConfig(),
  );
  return res.data;
};

// POST /prompt-library/favorites { prompt_id }
export const addFavorite = async (promptId) => {
  const res = await axios.post(
    `${baseUrl}/prompt-library/favorites`,
    { prompt_id: promptId },
    authConfig(),
  );
  return res.data;
};

// POST /prompt-library/favorites/custom { prompt, category? }
// category/audience default server-side ("My Prompts" / caller's business_function).
export const addCustomFavorite = async (prompt, category) => {
  const body = { prompt };
  if (category) body.category = category;
  const res = await axios.post(
    `${baseUrl}/prompt-library/favorites/custom`,
    body,
    authConfig(),
  );
  return res.data;
};

// DELETE /prompt-library/favorites/:favorite_id  (favorite_id is a string)
export const removeFavorite = async (favoriteId) => {
  const res = await axios.delete(
    `${baseUrl}/prompt-library/favorites/${favoriteId}`,
    authConfig(),
  );
  return res.data;
};
